<template>
    <el-dialog :visible="visible" class="ah-detail-dialog income-statement-detail-editor" @open="onOpen" @close="onClose" @closed="onClosed">
        <template slot="title">
            <div class="title">
                <span>{{ targetPurpose }}</span>
            </div>
        </template>

        <div class="detail-row" style="padding-top: 30px">
            <div style="margin-bottom: 20px">
                <el-form ref="form" :rules="rules" :model="form" label-position="left" label-width="160px">
                    <div style="margin: 0 auto 20px auto">
                        <el-form-item v-if="!!sno && !isTargetGroup" prop="fsno" label="所屬大群組">
                            <el-select v-model="form.fsno" filterable style="width: 320px">
                                <el-option v-for="option in grpList" :key="option.value" :label="option.label" :value="option.value"> </el-option>
                            </el-select>
                        </el-form-item>
                        <el-form-item prop="name" label="名稱">
                            <el-input v-model.trim="form.name" :maxlength="isTargetGroup ? 20 : 200" :disabled="!isAuth('F05') || isTargetSys" style="width: 220px" :show-word-limit="true" />
                        </el-form-item>
                        <el-form-item prop="description" label="說明">
                            <el-input v-model.trim="form.description" :disabled="!isAuth('F05') || isTargetSys" maxlength="50" show-word-limit />
                        </el-form-item>
                        <el-form-item v-if="!isTargetGroup && accountingItems" prop="accsno" label="歸屬會計明細科目">
                            <el-select v-model="form.accsno" size="small" filterable style="width: 320px">
                                <el-option v-for="option in accountingItems" :key="option.key" :label="option.label" :value="option.value">
                                    <span style="display: inline-block; width: 70px">{{ option.accno }}</span>
                                    <span style="display: inline-block">{{ option.accnam }}</span>
                                </el-option>
                            </el-select>
                        </el-form-item>
                        <el-form-item v-if="!isTargetGroup && accountingItems" prop="accsno">
                            <template slot="label">
                                其他建議科目
                                <ah-tip>
                                    <div>於編列收支項目時，可以選擇的會計明細科目選項。</div>
                                    <div>如未設定，則自動使用歸屬會計明細科目。</div>
                                </ah-tip>
                            </template>
                            <el-select v-model="form.suggestedAccItems" size="small" filterable reserve-keyword multiple style="width: 320px">
                                <el-option v-for="option in accountingItems" :key="option.key" :label="option.label" :value="option.accno">
                                    <span style="display: inline-block; width: 70px">{{ option.accno }}</span>
                                    <span style="display: inline-block">{{ option.accnam }}</span>
                                </el-option>
                            </el-select>
                        </el-form-item>
                        <template v-if="isTargetGroup">
                            <template v-if="isIncomeStatement && isD18Payed">
                                <el-form-item prop="incomeStatementType" label="綜合損益表">
                                    <el-select v-model="form.incomeStatementType" placeholder="請選擇" style="width: 195px">
                                        <el-option
                                            v-for="item in incomeStatementList"
                                            :label="`${item.id}. ${item.value}`"
                                            :value="item.id"
                                            :key="item.id"
                                            :disabled="!isIncomeOptionUsable(item.id)"
                                        ></el-option>
                                    </el-select>
                                </el-form-item>
                            </template>
                            <template v-if="!isIncomeStatement">
                                <template v-if="isD19Payed">
                                    <el-form-item prop="financeStatementType" label="財務分析表">
                                        <el-select v-model="form.financeStatementType" placeholder="請選擇" style="width: 195px">
                                            <el-option v-for="item in financeStatementList" :label="`${item.id}. ${item.value}`" :value="item.id" :key="item.id"></el-option>
                                        </el-select>
                                    </el-form-item>
                                    <el-form-item prop="financeStatementInitialPrice" label="財務分析初始金額">
                                        <MoneyInput v-model="form.financeStatementInitialPrice" allow-negative style="width: 195px" />
                                    </el-form-item>
                                </template>
                                <el-form-item prop="sync">
                                    <template slot="label"> 同步顯示於財務支出 </template>
                                    <el-checkbox v-model="form.sync" :disabled="isSyncDisabled || isTargetSys"> 啟用 </el-checkbox>
                                    <ah-tip v-if="isSyncDisabled">只適用於財務支出</ah-tip>
                                </el-form-item>
                            </template>
                        </template>
                    </div>
                </el-form>
            </div>
            <div class="main-action">
                <el-button v-if="isEditMode" @click="onDelete" :disabled="isTargetSys"> 刪除 </el-button>
                <div v-else></div>
                <div>
                    <el-button @click="onClose">取消</el-button>
                    <el-button type="primary" class="primary-action" @click="onSubmit">
                        <span v-if="isEditMode">儲存</span>
                        <span v-else>新增</span>
                    </el-button>
                </div>
            </div>
        </div>
    </el-dialog>
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';
import { financeStatementList, accountingSettingGroup, statementTypes } from '@/assets/commonData/options';

export default {
    props: {
        visible: Boolean,
        fullList: {
            type: Object,
            default: () => {},
        },
        isTop: Boolean,
        target: null,
        accountingItems: {
            type: Array,
            default: null,
        },
    },
    data() {
        const validator = (rule, value, callback) => {
            // 包含英文及數字_檢查
            const regExp = /\/+|\\+|\?+|\*+|\[+|\]+/;
            const isInvalidate = regExp.test(value);

            if (!value) callback(new Error('請輸入名稱'));
            else if (isInvalidate) callback(new Error('名稱請勿包含特殊字元 / \\ * ? [ ]'));
            else callback();
        };
        return {
            incomeStatementList: [],
            financeStatementList,

            sno: null,
            form: {
                fsno: null,
                name: '',
                description: null,
                accsno: null,
                suggestedAccItems: [],
                incomeStatementType: undefined,
                financeStatementType: undefined,
                financeStatementInitialPrice: 0,
                sync: false,
            },
            rules: {
                fsno: [{ required: true, validator }],
                name: [{ required: true, validator }],
            },
            incomeStateRelation: [
                {
                    category: 3, // 營業收入
                    canUseTypes: [4], // 4.營業收入
                },
                {
                    category: 4, // 營業支出
                    canUseTypes: [5, 6], // 5.營業成本 | 6.營業費用
                },
                {
                    category: 1, // 營業其他收入
                    canUseTypes: [7], // 7.營業外收入
                },
                {
                    category: 2, // 營業其他支出
                    canUseTypes: [6, 8], // 8.營業外支出
                },
            ],
        };
    },
    computed: {
        ...mapGetters({
            isAhooAgent: 'account/isAhooAgent',
            menuGroups: 'menu/menuGroups',
        }),
        targetPurpose() {
            return `${this.isEditMode ? '編輯' : '新增'}${this.target?.parent ? '子項目' : '大群組'} `;
        },
        isTargetGroup() {
            return !this.target?.parent;
        },
        isTargetSys() {
            return this.target?.iS_SYS;
        },
        isEditMode() {
            return this.sno !== null && this.sno !== undefined;
        },
        isIncomeStatement() {
            return this.target?.category === 3 || this.target?.category === 4 || this.target?.category === 1 || this.target?.category === 2;
        },
        isSyncDisabled() {
            return this.target?.category !== 5;
        },
        isD18Payed() {
            if (this.isAhooAgent) return true;
            return this.isPayed('D18');
        },
        isD19Payed() {
            if (this.isAhooAgent) return true;
            return this.isPayed('D19');
        },

        grpList() {
            if (!this.target?.category) return [];

            const statementTypesKey = _.find(statementTypes, ['type', this.target.category])?.key;
            if (!statementTypesKey) return [];

            const RETURN = _.chain(this.fullList)
                .get(statementTypesKey)
                .map((o) => ({ value: o.sno, label: o.iteM_NAM }))
                .value();

            return RETURN;
        },
    },
    mounted() {
        this.init();
    },
    methods: {
        init() {
            this.incomeStatementList = [...accountingSettingGroup.filter((o) => o.id > 3 && o.id < 9)];
        },
        isIncomeOptionUsable(incomeStateId) {
            const cate = this.incomeStateRelation.find((x) => x.category === this.target.category);
            return cate.canUseTypes.includes(incomeStateId);
        },
        onOpen() {
            this.sno = this.target.sno;
            if (!this.sno) {
                // 新增群組預選項目
                if (!this.target.parent) {
                    const cate = this.incomeStateRelation.find((x) => x.category === this.target.category);

                    if (cate) {
                        const [first] = cate.canUseTypes;
                        this.form.incomeStatementType = first;
                    }
                } else this.form.fsno = this.target.parent;

                return;
            }

            console.log(this.target);

            this.form.fsno = this.target.fsno;
            this.form.name = this.target.iteM_NAM;
            this.form.description = this.target.grP_MEM;
            this.form.accsno = this.target.accsno;
            this.form.suggestedAccItems = _.map(this.target.suggestedAccItems, 'accno');

            console.log(this.form);

            if (this.isTargetGroup) {
                this.form.sync = this.target.isshare;

                if (this.isIncomeStatement) {
                    this.form.incomeStatementType = this.target.grP_NAM || undefined;
                } else {
                    this.form.financeStatementType = this.target.grP_NAM || undefined;
                    this.form.financeStatementInitialPrice = this.target.grP_AMT;
                }
            }
        },
        onClose() {
            this.$emit('close');
        },
        onClosed() {
            this.$emit('closed');
            this.form = {
                fsno: null,
                name: '',
                description: null,
                accsno: null,
                suggestedAccItems: [],
                incomeStatementType: undefined,
                financeStatementType: undefined,
                financeStatementInitialPrice: 0,
                sync: false,
            };
            this.$refs.form.resetFields();
        },
        async onSubmit() {
            this.$refs.form.validate(async (valid) => {
                if (!valid) return false;

                const action = this.sno ? 'Update' : 'Add';
                if (this.isTargetGroup) this.onMutateGroup(action);
                else this.onMutateItem(action);

                return true;
            });
        },
        async onMutateGroup(action) {
            const targetFieldName = action === 'Add' ? 'type' : 'sno';
            const targetData = action === 'Add' ? this.target.category : this.target.sno;
            const groupName = this.isIncomeStatement ? this.form.incomeStatementType : this.form.financeStatementType;
            const groupAmt = this.isIncomeStatement ? undefined : this.form.financeStatementInitialPrice;

            if (this.isNameDuplicated()) return;

            try {
                await this.$api({
                    url: `/IncomeStatementSettingPage/${action}Group`,
                    method: 'POST',
                    data: {
                        [targetFieldName]: targetData,
                        iteM_NAM: this.form.name,
                        isshare: this.form.sync,
                        grP_NAM: groupName,
                        grP_MEM: this.form.description,
                        grP_AMT: groupAmt,
                        isTop: action === 'Add' ? this.isTop : undefined,
                    },
                });
                this.$emit('update');
                this.onClose();
                this.$message({ type: 'success', message: '已儲存', duration: 1000 });
            } catch (e) {
                console.log(e);
                this.$message.error('儲存失敗');
            }
        },

        async onMutateItem(action) {
            const targetFieldName = action === 'Add' ? 'fsno' : 'sno';
            const targetData = action === 'Add' ? this.target.parent : this.target.sno;

            if (this.isNameDuplicated(this.form.fsno)) return;

            try {
                await this.$api({
                    url: `/IncomeStatementSettingPage/${action}Item`,
                    method: 'POST',
                    data: {
                        [targetFieldName]: targetData,
                        fsno: this.form.fsno,
                        iteM_NAM: this.form.name,
                        grP_MEM: this.form.description,
                        isTop: action === 'Add' ? this.isTop : undefined,
                        accsno: this.form.accsno,
                        suggestedAccItems: this.form.suggestedAccItems,
                    },
                });
                this.$emit('update');
                this.onClose();
                this.$message({ type: 'success', message: '已儲存', duration: 1000 });
            } catch (e) {
                console.log(e);
                this.$message.error('儲存失敗');
            }
        },

        isNameDuplicated(fsno) {
            const statementTypesKey = _.find(statementTypes, ['type', this.target.category])?.key;

            const DATA = fsno ? _.find(this.fullList[statementTypesKey], ['sno', fsno])?.childSettings : this.fullList[statementTypesKey];

            const found = _.chain(DATA)
                .filter((o) => o.sno !== this.target.sno)
                .find(['iteM_NAM', this.form.name])
                .value();

            if (found) {
                this.$message({
                    showClose: true,
                    message: `${fsno ? '子項目' : '群組'}名稱不可重複`,
                    type: 'error',
                });
                return true;
            }
            return false;
        },

        async onDelete() {
            try {
                await this.$confirm('確定要刪除？', {
                    confirmButtonText: '確定',
                    cancelButtonText: '取消',
                });
                this.$emit('delete', {
                    sno: this.sno,
                    isGroup: this.isTargetGroup,
                });
            } catch (e) {
                console.log(e);
            }
        },

        onEditDialogPressEnter(event) {
            this.onClose();
            event.stopPropagation();
            event.preventDefault();
        },
    },
};
</script>

<style lang="scss">
.ah-detail-dialog.income-statement-detail-editor {
    .el-dialog {
        max-width: 660px;
    }
    .title {
        text-align: center;
    }
    .main-action {
        display: flex;
        justify-content: space-between;
    }
}
</style>
